import { MenuItem, Select, TextField, Typography } from '@material-ui/core'
import { CustomTooltip } from 'components/facets'
import { ChangesInterface, useLocationStore } from 'lib/hooks/useLocationStore'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CardExtraAttribute,
  CARD_LANGUAGES_WITH_FLAGS,
  CONDITIONS_MAP_WITH_LABELS,
  CountryEdition,
  countryEditionMap,
  FINISH_TYPE_LABELS,
  InventoryArticle,
} from 'shared'
import { ExtraAttributesValuesAndImagesMapInterface } from 'utils/constants'

export interface LocationsPanelNewRowProps {
  article: InventoryArticle
  location: any
  rowId: string
  deleteNewRow: any
  gameInformations: ExtraAttributesValuesAndImagesMapInterface
  isSingle: boolean
}

export const LocationsPanelNewRow = ({
  article,
  location,
  rowId,
  deleteNewRow,
  gameInformations,
  isSingle,
}: LocationsPanelNewRowProps): JSX.Element => {
  const { t } = useTranslation()
  const initialAttributes = {
    deltaQuantity: location.deltaQuantity,
    condition: article.newCondition || location.condition,
    idLanguage: article.newIdLanguage || location.idLanguage,
    finishType: article.finishType,
    countryEdition: article.countryEdition,
  }
  const initialExtraAttributes = {
    isSigned: location.isSigned,
    isFirstEd: location.isFirstEd,
  }

  const [deltaQuantity, setDeltaQuantity] = useState(initialAttributes.deltaQuantity)
  const [newCondition, setNewCondition] = useState(initialAttributes.condition)
  const [newIdLanguage, setNewIdLanguage] = useState(initialAttributes.idLanguage)
  const [newSigned, setNewSigned] = useState(initialExtraAttributes.isSigned)
  const [newFinishType, setNewFinishType] = useState(
    initialAttributes.finishType ?? FINISH_TYPE_LABELS.REGULAR
  )
  const [newCountryEdition, setNewCountryEdition] = useState(
    initialAttributes.countryEdition ?? CountryEdition.Regular
  )

  const [newFirstEdition, setNewFirstEdition] = useState(initialExtraAttributes.isFirstEd)
  const [locationName, setLocationName] = useState(location.locationName)
  const { currentValues, setCurrentValues, newRows, setNewRows } = useLocationStore(
    (state) => state
  )

  const displayCellOrDoActions = (attribute: CardExtraAttribute): boolean => {
    if (!isSingle && attribute != CardExtraAttribute.FirstEd) {
      return false
    }

    if (gameInformations.cardExtraAttribute.some((element) => element.attribute === attribute))
      return true
    return false
  }

  const subtractDeltaQuantity = () => {
    if (deltaQuantity > 1) {
      setDeltaQuantity((deltaQuantity: number) => deltaQuantity - 1)
    }
  }

  const addDeltaQuantity = () => {
    setDeltaQuantity((deltaQuantity: number) => deltaQuantity + 1)
  }

  const convertValueFromSelectionToStateValue = (value: any) => {
    if (value === 'true' || value === 'Yes' || value === true) return true
    if (value === 'false' || value === 'No' || value === false) return false
    return value
  }

  useEffect(() => {
    ///update all values for current row
    const newCurrentValues: ChangesInterface = { ...currentValues }
    newCurrentValues[rowId] = {
      quantity: 0,
      idLanguage: newIdLanguage,
      condition: newCondition,
      isSigned: newSigned,
      isFirstEd: newFirstEdition,
      locationName: locationName,
      finishType: newFinishType,
      countryEdition: newCountryEdition,
      deltaQuantity,
      isNewRow: true,
      rowId: rowId,
    }

    setCurrentValues(newCurrentValues)

    let copyOfNewRows = [...newRows]
    copyOfNewRows = copyOfNewRows.map((row) => {
      if (row.rowId === newCurrentValues[rowId].rowId) {
        return newCurrentValues[rowId]
      }
      return row
    })

    setNewRows(copyOfNewRows)
  }, [
    deltaQuantity,
    newCondition,
    newIdLanguage,
    newFirstEdition,
    newFinishType,
    newCountryEdition,
    newSigned,
    locationName,
  ])

  const finishTypes = gameInformations.finishType
    .filter((ft) => ft[isSingle ? 'singles' : 'sealed'])
    .map((ft) => ft.label)

  return (
    <tr key={location.name} className="location-panel-row newRow">
      {/* quantity */}
      <td align="center">
        <Typography> </Typography>
      </td>
      {/* change quantity */}
      <td align="center">
        <div className="d-flex flex-row justify-content-center align-items-center">
          <i
            data-testid={`decrease-quantity-${rowId}`}
            className="fa-light fa-circle-minus interactions-button"
            onClick={() => subtractDeltaQuantity()}
          ></i>
          <Typography data-testid={`change-quantity-${rowId}`} className="mx-2">
            {deltaQuantity}
          </Typography>
          <i
            data-testid={`increase-quantity-${rowId}`}
            className="fa-light fa-circle-plus interactions-button"
            onClick={() => addDeltaQuantity()}
          ></i>
        </div>
      </td>

      {/* edit quantity */}
      <td width={65} align="center">
        <Typography> </Typography>
      </td>

      {/* language */}
      <td width={120} align="center">
        <Select
          className="locations-panel"
          data-testid={`language-${rowId}`}
          value={newIdLanguage}
          onChange={(e: any) => setNewIdLanguage(e.target.value)}
          style={{ width: '60px' }}
        >
          {CARD_LANGUAGES_WITH_FLAGS.map((language) => (
            <MenuItem
              data-testid={`languageValue-${language.value}`}
              key={`${rowId}-${language.value}`}
              value={language.value}
            >
              {language.label}
            </MenuItem>
          ))}
        </Select>
      </td>

      {/* condition */}
      {isSingle && (
        <td width={120} align="center">
          <Select
            className="locations-panel"
            data-testid={`condition-${rowId}`}
            value={newCondition}
            onChange={(e: any) => setNewCondition(e.target.value)}
          >
            {CONDITIONS_MAP_WITH_LABELS.map((c) => (
              <MenuItem key={`${locationName}-${c}`} value={c.code}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </td>
      )}

      {/* attributes games */}
      {finishTypes.length > 0 && (
        <td width={120} align="center">
          <Select
            fullWidth
            className="locations-panel"
            data-testid={`${FINISH_TYPE_LABELS.FOIL}-${rowId}`}
            value={newFinishType}
            onChange={(e) => {
              setNewFinishType(e.target.value as FINISH_TYPE_LABELS)
            }}
          >
            {finishTypes.map((attr) => (
              <MenuItem key={attr} value={attr}>
                {attr.replace(/^is/, '')}
              </MenuItem>
            ))}
          </Select>
        </td>
      )}

      {gameInformations.countryEdition.length > 0 && (
        <td width={120} align="center">
          <Select
            fullWidth
            className="locations-panel"
            data-testid={`countryEdition-${locationName}`}
            value={newCountryEdition}
            onChange={(e) => {
              setNewCountryEdition(e.target.value as CountryEdition)
            }}
          >
            {countryEditionMap.map((entry) => (
              <MenuItem key={entry.code} value={entry.value}>
                {entry.label}
              </MenuItem>
            ))}
          </Select>
        </td>
      )}

      {/* signed */}
      {displayCellOrDoActions(CardExtraAttribute.Signed) ? (
        <td width={120} align="center">
          <Select
            className="locations-panel"
            data-testid={`${CardExtraAttribute.Signed}-${rowId}`}
            value={newSigned}
            onChange={(e) => {
              setNewSigned(convertValueFromSelectionToStateValue(e.target.value))
            }}
          >
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </Select>
        </td>
      ) : null}

      {/* first edition */}
      {displayCellOrDoActions(CardExtraAttribute.FirstEd) ? (
        <td width={120} align="center">
          <Select
            className="locations-panel"
            data-testid={`${CardExtraAttribute.FirstEd}-${rowId}`}
            value={newFirstEdition}
            onChange={(e) => {
              setNewFirstEdition(convertValueFromSelectionToStateValue(e.target.value))
            }}
          >
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </Select>
        </td>
      ) : null}

      {/* location */}
      <td width={130} align="center">
        <TextField
          className="locations-panel"
          data-testid={`location-${rowId}`}
          placeholder="Default"
          value={locationName ? locationName : ''}
          onChange={(e) => setLocationName(e.target.value)}
          style={{ width: '100px', textAlignLast: 'center' }}
        />
      </td>

      {/*reset button */}
      <td width={140} align="center">
        <CustomTooltip title={`${t('common.cancel')}`}>
          <i
            className={`fa-light fa-trash table-action-icon`}
            onClick={() => deleteNewRow(rowId)}
          ></i>
        </CustomTooltip>
      </td>
    </tr>
  )
}
